<template>
  <div id="quality-page">
    <div class="header-block d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <div class="page-title">Qualidade</div>
        <div class="date-range-info" v-if="dateRangeText && !hasError">{{ dateRangeText }}</div>
        <div class="error-info" v-if="hasError">
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-25" />
          Erro ao buscar os dados
        </div>
      </div>
      <b-button class="btn-filters" variant="outline-primary" size="sm" @click="filtersOpen = true">
        <feather-icon icon="FilterIcon" class="mr-50" />
        Filtros
      </b-button>
    </div>

    <div class="body-block">
      <b-row>
        <!-- Charts area -->
        <b-col cols="12" class="charts-col">
          <!-- KPIs Row -->
          <b-row class="kpi-row">
            <b-col v-for="metric in kpis" :key="metric.key" cols="12" sm="6" md="6" lg="6" xl="6" class="kpi-col">
              <b-card class="kpi-card">
                <div class="kpi-title">{{ metric.title }}</div>
                <div class="kpi-donut">
                  <vc-donut :size="kpiSize" :thickness="12" :start-angle="-90" :total="metric.total"
                    :sections="[{ value: metric.value, color: metric.color }]" :foreground="kpiTrack"
                    :background="kpiBg">
                    <div class="kpi-center">
                      <div class="kpi-value">{{ metric.display }}</div>
                    </div>
                  </vc-donut>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row class="chart-row">
            <!-- Esquerda: Peças por Recurso -->
            <b-col cols="12" :md="hasRefuseReasons ? 8 : 12" class="mb-1">
              <b-card class="chart-card h-100">
                <div class="chart-header">Peças Boas e Peças Ruins por Recurso</div>
                <app-echart-bar :optionData="barPerResource" :height="barHeight" />
              </b-card>
            </b-col>

            <!-- Direita: Motivo do Refugo -->
            <b-col v-if="hasRefuseReasons" cols="12" md="4" class="mb-1">
              <b-card class="chart-card h-100">
                <div class="chart-header">Motivo do Refugo</div>
                <app-echart-bar :optionData="refuseReasonOption" :height="barHeight" />
              </b-card>
            </b-col>
          </b-row>

          <b-row class="chart-row">
            <b-col cols="12" class="mb-1">
              <b-card class="chart-card h-100">
                <div class="chart-header">Retrabalho por Recurso</div>
                <app-echart-bar :optionData="reworkByResourceOption" :height="barHeight" />
              </b-card>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <!-- Right sidebar filters -->
    <b-sidebar right shadow bg-variant="white" width="320px" v-model="filtersOpen" backdrop>
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100 px-1 py-1">
          <span class="font-weight-bold">Filtros</span>
          <span style="cursor:pointer" @click="filtersOpen = false">
            <feather-icon icon="XIcon" size="18" />
          </span>
        </div>
      </template>
      <div class="filters-card">
        <div class="filter">
          <label class="filter-label">Filtro Rápido:</label>
          <div class="quick-filter-buttons">
            <button v-for="days in quickFilterDays" :key="days"
              :class="['quick-filter-btn', { active: filterDays === days }]" @click="setQuickFilter(days)">
              {{ days }} dias
            </button>
          </div>
        </div>
        <div class="filter">
          <label class="filter-label">De:</label>
          <input type="date" v-model="startDate" class="date-input-sidebar" @change="onManualDateChange" />
        </div>
        <div class="filter">
          <label class="filter-label">Até:</label>
          <input type="date" v-model="endDate" class="date-input-sidebar" @change="onManualDateChange" />
        </div>
        <div class="filter" v-for="f in visibleFilters" :key="f.key">
          <label class="filter-label">{{ f.label }}</label>
          <b-form-select v-model="f.value" :options="f.options" size="sm" />
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end px-1 pb-1">
          <b-button size="sm" class="mr-50" variant="outline-secondary" @click="clearFilters">Limpar</b-button>
          <b-button size="sm" variant="primary" @click="applyFilters">Aplicar</b-button>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import { BRow, BCol, BCard, BFormSelect, BButton, BButtonGroup, BSidebar } from 'bootstrap-vue';
import { mapMutations } from 'vuex';
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue';
import { useFilters } from '@/views/hooks/useFilters';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import { loadResources } from '@/views/utils/getResources';
export default {
  name: 'QualityPage',
  components: { BRow, BCol, BCard, BFormSelect, BButton, BButtonGroup, BSidebar, AppEchartBar, FeatherIcon },
  data() {
    const filtersHelper = useFilters();

    return {
      hasRefuseReasons: false,
      brandColor: '#f3811a',
      kpiColor: '#f3811a',
      kpiTrack: '#E9ECEF',
      kpiBg: '#ffffff',
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      filtersOpen: false,
      dateRangeText: '',
      hasError: false,
      startDate: '',
      endDate: '',
      filterDays: 7,
      quickFilterDays: [7, 30, 60, 90],

      filters: filtersHelper.createFilters(),
      filtersHelper,
      resourceMap: {}, // Mapeamento de nome do recurso para ID
      areaMap: {},
      reworkColorPalette: [
        '#f3811a', '#28c76f', '#ea5455', '#00cfe8', '#7367f0',
        '#ff9f43', '#1e90ff', '#9c27b0', '#2ecc71', '#34495e'
      ],
      reworkResourceColors: {},

      kpis: [
        { key: 'good', title: 'Peças Boas', value: 0, total: 10, display: '0', color: '#28c76f' },
        { key: 'bad', title: 'Peças Ruins', value: 0, total: 10, display: '0', color: '#ea5455' },
        // { key: 'badProb', title: 'Probabilidade de Peça Ruim (%)', value: 57.54, total: 100, display: '57.54' }
      ],
      barPerResource: {
        grid: { left: '40px', right: '10px', bottom: '20px', top: '50px', containLabel: true },
        xAxis: {
          type: 'category',
          axisLabel: { rotate: 45, fontSize: 10 },
          data: []
        },
        yAxis: { type: 'value', splitLine: { show: false } },
        series: [
          { name: 'Peças Boas', type: 'bar', data: [], itemStyle: { color: '#28c76f' }, barMaxWidth: 40 },
          { name: 'Peças Ruins', type: 'bar', data: [], itemStyle: { color: '#ea5455' }, barMaxWidth: 40 }
        ]
      },
      refuseReasonOption: {
        grid: { left: '20px', right: '10px', bottom: '20px', top: '50px', containLabel: true },
        yAxis: { type: 'value', splitLine: { show: false } },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: { rotate: 45, fontSize: 10 },
        },
        series: [
          { name: 'Motivo', type: 'bar', data: [], itemStyle: { color: '#f3811a' }, barMaxWidth: 40 }
        ]
      },
      reworkByResourceOption: {
        grid: { left: '40px', right: '10px', bottom: '20px', top: '50px', containLabel: true },
        xAxis: {
          type: 'category',
          axisLabel: { rotate: 45, fontSize: 10 },
          data: []
        },
        yAxis: { type: 'value', splitLine: { show: false } },
        series: [
          { name: 'Retrabalho', type: 'bar', data: [], itemStyle: { color: '#ff9f43' }, barMaxWidth: 40 }
        ]
      }
    };
  },
  mounted() {
    this.initializeDates();
    this.loadQualityData();
    if (typeof window !== 'undefined') {
      this._resizeHandler = () => (this.windowWidth = window.innerWidth);
      window.addEventListener('resize', this._resizeHandler);
    }
  },
  beforeDestroy() {
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },
  computed: {
    visibleFilters() {
      return this.filters.filter(f => !['year', 'month', 'day'].includes(f.key));
    },
    kpiSize() {
      const w = this.windowWidth;
      if (w >= 1400) return 92;
      if (w >= 1200) return 82;
      if (w >= 992) return 72;
      if (w >= 768) return 62;
      return 56;
    },
    barHeight() {
      const w = this.windowWidth;
      if (w >= 1400) return 420;
      if (w >= 1200) return 380;
      if (w >= 992) return 340;
      if (w >= 768) return 300;
      return 260;
    },
    lineHeight() {
      const w = this.windowWidth;
      if (w >= 1400) return 320;
      if (w >= 1200) return 300;
      if (w >= 992) return 280;
      if (w >= 768) return 260;
      return 240;
    }
  },
  methods: {
    ...mapMutations('app', ['UPDATE_FLAG_SHOW_OVERLAY']),

    async loadQualityData() {
      this.UPDATE_FLAG_SHOW_OVERLAY(true);

      const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

      if (!siteId) {
        console.warn('Nenhum site selecionado para carregar dados de Qualidade.');
        this.UPDATE_FLAG_SHOW_OVERLAY(false);
        return;
      }

      try {
        // Construir os parâmetros da requisição
        const params = { SiteId: siteId };
        this.loadWorkcenters();
        this.loadResourcesList();

        // filtro de dias
        if (this.filterDays) {
          params.FilterDays = this.filterDays;
        }

        // Adicionar datas se estiverem definidas
        if (this.startDate && this.endDate) {
          params.StartDate = this.startDate;
          params.EndDate = this.endDate;
        }

        // filtro de recurso
        const selectedResource = this.filtersHelper.getFilterValue(this.filters, 'resource');
        if (selectedResource && selectedResource !== 'Todos' && this.resourceMap[selectedResource]) {
          params.ResourcesIds = String(this.resourceMap[selectedResource]);
        }

        // filtro de area
        const selectedArea = this.filtersHelper.getFilterValue(this.filters, 'area');
        if (selectedArea && selectedArea !== 'Todos' && this.areaMap[selectedArea]) {
          params.WorkcentersIds = String(this.areaMap[selectedArea]);
        }

        // Atualizar o texto do range de datas
        this.updateDateRangeText();



        const response = await this.$http2.get("/api/quality/calculate", {
          params: params
        });

        this.applyQualityResponse(response?.data?.data || {});
        this.hasError = false;
      } catch (error) {
        console.error('Erro ao carregar dados de Qualidade', error);
        this.hasError = true;
      } finally {
        this.UPDATE_FLAG_SHOW_OVERLAY(false);
      }
    },

    // TODO: AQUI QUANDO SUBIR PRA PROD VAI PUXAR DA FUNCAO GETWORKCENTERS
    async loadWorkcenters() {
      const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

      if (!siteId) {
        console.warn("Nenhum site selecionado para carregar dados de OEE.");
        return;
      }

      const workcenters = await this.$http2.get("/api/myconfig/workcenters", {
        params: {
          siteId
        }
      });

      const areaNames = workcenters?.data?.data?.map(item => item?.workcenterName) || [];
      this.filtersHelper.updateFilterOptions(this.filters, 'area', areaNames, true);

      // setando o map de areas
      this.areaMap = Object.fromEntries(workcenters?.data?.data?.map(item => [item.workcenterName, item.workcenterId]) || []);
    },

    getCurrentSiteId() {
      const storedSite = this.safeJSONParse(localStorage.getItem('currentSite')) || null;
      const cookieSite = this.$cookies.get('userInfo')?.currentSite || null;
      const resolvedSite = storedSite || cookieSite;

      return resolvedSite?.id ?? resolvedSite?.siteId ?? null;
    },

    applyQualityResponse(payload = {}) {
      const {
        qualityGlobal = {},
        qualityByResource = [],
        qualityByArea = [],
        qualityByShift = [],
        qualityByReferenceDate = []
      } = payload || {};

      // Construir resourceMap (nome -> ID) para enviar na próxima requisição
      qualityByResource.forEach(item => {
        if (item?.resourceName && item?.resourceId) {
          this.resourceMap[item.resourceName] = item.resourceId;
        }
      });

      //adiciona os turnos no filtro
      const shiftLabels = qualityByShift.map(item => item?.shiftName || 'Sem nome');
      this.filtersHelper.updateFilterOptions(this.filters, 'shift', shiftLabels, true);


      const shiftFilter = this.filtersHelper.getFilterValue(this.filters, 'shift');
      if (shiftFilter && shiftFilter !== 'Todos') {
        this.filterByShift(qualityByShift, shiftFilter);
        // console.log('shiftFilter', shiftFilter);
        return;
      }

      // Atualizar KPIs
      this.updateKpi('good', qualityGlobal.totalGoodPieces || 0);
      this.updateKpi('bad', qualityGlobal.totalBadPieces || 0);

      // Atualizar gráfico de peças por recurso
      this.updateResourceChart(qualityByResource);

      // Atualizar gráfico de retrabalho por recurso
      this.updateReworkChart(qualityByResource);

      // Atualizar gráfico de motivos de refugo
      this.updateRefuseReasonChart(qualityGlobal.topRefuseReasons || []);
    },


    //filtro por turno
    filterByShift(qualityByShift = [], shiftFilter = 'Todos') {
      const filtered = qualityByShift.filter(r => r.shiftName === shiftFilter);
      this.updateKpi('good', qualityByShift.find(r => r.shiftName === shiftFilter)?.goodPieces || 0);
      this.updateKpi('bad', qualityByShift.find(r => r.shiftName === shiftFilter)?.badPieces || 0);
      this.updateResourceChart(filtered);
      this.updateReworkChart(filtered);
      this.updateRefuseReasonChart(qualityByShift.refuseReasons || []);
    },

    updateKpi(key, value) {
      const kpi = this.kpis.find(item => item.key === key);
      if (!kpi) return;

      kpi.value = value;

      // Formatar o display e normalizar o valor baseado na magnitude
      if (value >= 1000000) {
        const normalizedValue = value / 1000000;
        kpi.value = normalizedValue;  // ← MUDANÇA: valor normalizado
        kpi.display = `${normalizedValue.toFixed(2)} Mi`;
        kpi.total = Math.ceil(normalizedValue) + 10;
      } else if (value >= 1000) {
        const normalizedValue = value / 1000;
        kpi.value = normalizedValue;  // ← MUDANÇA: valor normalizado
        kpi.display = `${normalizedValue.toFixed(2)} K`;
        kpi.total = Math.ceil(normalizedValue) + 10;
      } else {
        kpi.value = value;  // valores pequenos permanecem iguais
        kpi.display = value.toFixed(0);
        kpi.total = value + 10;
      }
    },

    updateResourceChart(qualityByResource = []) {
      if (!Array.isArray(qualityByResource) || qualityByResource.length === 0) {
        this.barPerResource.xAxis.data = [];
        this.barPerResource.series[0].data = [];
        this.barPerResource.series[1].data = [];
        return;
      }

      // Extrair labels (nomes dos recursos)
      const resourceLabels = qualityByResource.map((item, index) =>
        item?.resourceName ||
        item?.resource ||
        item?.name ||
        item?.id ||
        `Recurso ${index + 1}`
      );

      // Extrair valores de peças boas e ruins
      const goodPieces = qualityByResource.map(item => item?.goodPieces || 0);
      const badPieces = qualityByResource.map(item => item?.badPieces || 0);

      // Atualizar o gráfico
      this.barPerResource.xAxis.data = resourceLabels;
      this.barPerResource.series[0].data = goodPieces;
      this.barPerResource.series[1].data = badPieces;
    },

    updateReworkChart(qualityByResource = []) {
      if (!Array.isArray(qualityByResource) || qualityByResource.length === 0) {
        this.reworkByResourceOption.xAxis.data = [];
        this.reworkByResourceOption.series[0].data = [];
        return;
      }

      const resourceLabels = qualityByResource.map((item, index) =>
        item?.resourceName ||
        item?.resource ||
        item?.name ||
        item?.id ||
        `Recurso ${index + 1}`
      );

      const reworkValues = qualityByResource.map(item =>
        Number(item?.reworkQuantity ?? item?.reworkquantity ?? 0)
      );

      const coloredData = resourceLabels.map((label, index) => ({
        value: reworkValues[index] ?? 0,
        itemStyle: { color: this.getReworkResourceColor(label) }
      }));

      this.reworkByResourceOption.xAxis.data = resourceLabels;
      this.reworkByResourceOption.series[0].data = coloredData;
    },

    getReworkResourceColor(resourceName) {
      const key = resourceName || 'default';
      if (this.reworkResourceColors[key]) return this.reworkResourceColors[key];
      const used = new Set(Object.values(this.reworkResourceColors));
      const available = this.reworkColorPalette.filter(c => !used.has(c));
      const pool = available.length ? available : this.reworkColorPalette;
      const color = pool[Math.floor(Math.random() * pool.length)];
      this.reworkResourceColors[key] = color;
      return color;
    },

    updateRefuseReasonChart(topRefuseReasons = []) {
      const hasData = Array.isArray(topRefuseReasons) && topRefuseReasons.some(r => (r?.quantity || r?.count || r?.total || 0) > 0);
      this.hasRefuseReasons = hasData;

      // console.log('topRefuseReasons', topRefuseReasons);
      // console.log('hasData', hasData);
      if (!hasData) {
        // Limpa dados do gráfico quando não houver motivos
        this.refuseReasonOption.xAxis.data = [];
        this.refuseReasonOption.series[0].data = [];
        return;
      }

      // Extrair labels (motivos de refugo)
      const reasonLabels = topRefuseReasons.map((item, index) =>
        item?.refuseReasonName ||
        `Motivo ${index + 1}`
      );

      // Extrair quantidades
      const reasonValues = topRefuseReasons.map(item =>
        item?.quantity ||
        item?.count ||
        item?.total ||
        0
      );

      // Atualizar o gráfico
      this.refuseReasonOption.xAxis.data = reasonLabels;
      this.refuseReasonOption.series[0].data = reasonValues;
    },

    activeFilters() {
      return {
        year: this.filters.find(f => f.key === 'year').value,
        month: this.filters.find(f => f.key === 'month').value,
        day: this.filters.find(f => f.key === 'day').value,
        shift: this.filters.find(f => f.key === 'shift').value,
        area: this.filters.find(f => f.key === 'area')?.value || 'Todos',
        resource: this.filters.find(f => f.key === 'resource')?.value || 'Todos'
      };
    },

    getDateRangeFromFilters() {
      const yearFilter = this.filters.find(f => f.key === 'year')?.value;
      const monthFilter = this.filters.find(f => f.key === 'month')?.value;
      const dayFilter = this.filters.find(f => f.key === 'day')?.value;

      if (yearFilter === 'Todos' && monthFilter === 'Todos' && dayFilter === 'Todos') {
        return null;
      }

      const year = yearFilter;

      const date = new Date();

      // Mapeamento de meses para números
      const monthMap = {
        'Jan': '01', 'Fev': '02', 'Mar': '03', 'Abr': '04',
        'Mai': '05', 'Jun': '06', 'Jul': '07', 'Ago': '08',
        'Set': '09', 'Out': '10', 'Nov': '11', 'Dez': '12'
      };

      // CASO 1: Apenas ano selecionado
      if ((!monthFilter || monthFilter === 'Todos') && (!dayFilter || dayFilter === 'Todos')) {
        return {
          StartDate: `${year}-01-01`,
          EndDate: `${year}-12-31`
        };
      }

      const month = monthMap[monthFilter];

      // apenas mês selecionado
      if ((!yearFilter || yearFilter === 'Todos') && (!dayFilter || dayFilter === 'Todos')) {
        return {
          StartDate: `${date.getFullYear()}-${month}-01`,
          EndDate: `${date.getFullYear()}-${month}-31`
        };
      }

      //apenas dia selecionado
      if ((!yearFilter || yearFilter === 'Todos') && (!monthFilter || monthFilter === 'Todos')) {
        return {
          StartDate: `${date.getFullYear()}-${date.getMonth() + 1}-${dayFilter}T00:00:00`,
          EndDate: `${date.getFullYear()}-${date.getMonth() + 1}-${dayFilter}T23:59:59`
        };
      }

      // Ano e mês selecionados, mas não dia
      if (!dayFilter || dayFilter === 'Todos') {
        // Calcula o último dia do mês
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        const lastDayFormatted = String(lastDay).padStart(2, '0');

        return {
          StartDate: `${year}-${month}-01`,
          EndDate: `${year}-${month}-${lastDayFormatted}`
        };
      }

      //Ano, mês e dia selecionados
      const day = String(dayFilter).padStart(2, '0');

      return {
        StartDate: `${year}-${month}-${day}`,
        EndDate: `${year}-${month}-${day}`
      };
    },

    applyFilters() {
      this.filtersOpen = false;
      this.loadQualityData();
    },

    clearFilters() {
      this.filters.forEach(ff => ff.value = 'Todos');
      this.initializeDates();
      this.loadQualityData();
    },

    safeJSONParse(value) {
      try {
        return value ? JSON.parse(value) : null;
      } catch (error) {
        return null;
      }
    },

    initializeDates() {
      // Inicializar com os últimos 7 dias
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);

      this.startDate = this.formatDateToISO(startDate);
      this.endDate = this.formatDateToISO(endDate);
    },

    formatDateToISO(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    async loadResourcesList() {
      const resources = await loadResources(this.siteId, this.$http2);
      // console.log('resources', resources);
      const resourcesNames = resources?.map(item => item?.name) || [];
      // console.log('resourcesNames', resourcesNames);
      this.filtersHelper.updateFilterOptions(this.filters, 'resource', resourcesNames, true);

      // setando o map de recursos
      this.resourceMap = Object.fromEntries(resources?.map(item => [item.name, item.id]) || []);
    },
    updateDateRangeText() {
      if (!this.startDate || !this.endDate) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);

        this.dateRangeText = `${this.formatDateToBrazilian(startDate)} a ${this.formatDateToBrazilian(endDate)}`;
      } else {
        const startDate = this.parseLocalDate(this.startDate);
        const endDate = this.parseLocalDate(this.endDate);

        this.dateRangeText = `${this.formatDateToBrazilian(startDate)} a ${this.formatDateToBrazilian(endDate)}`;
      }
    },

    parseLocalDate(dateStr) {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return new Date(dateStr.getTime());
      const str = String(dateStr);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-').map(Number);
        return new Date(y, m - 1, d);
      }
      return new Date(dateStr);
    },

    formatDateToBrazilian(date) {
      if (!date) return '';

      const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? this.parseLocalDate(date)
        : new Date(date);
      if (!d || Number.isNaN(d.getTime())) return '';

      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();

      return `${day}-${month}-${year}`;
    },

    setQuickFilter(days) {
      this.filterDays = days;

      // Atualizar as datas baseado no filtro rápido
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      this.startDate = this.formatDateToISO(startDate);
      this.endDate = this.formatDateToISO(endDate);
    },

    onManualDateChange() {
      // Quando o usuário alterar manualmente as datas, limpar o filterDays
      this.filterDays = null;
    }
  }
};
</script>

<style lang="scss" scoped>
#quality-page {
  .header-block {
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .tabs {
      .tab-btn {
        font-weight: 600;

        &.active {
          background-color: #f3811a;
          border-color: #f3811a;
        }
      }
    }
  }

  .page-title {
    font-size: 18px;
    font-weight: 700;
    color: #4c4541;
  }

  .date-range-info {
    font-size: 12px;
    color: #6c757d;
    margin-left: 12px;
    font-weight: 500;
  }

  .error-info {
    font-size: 12px;
    color: #ea5455;
    margin-left: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
  }

  .body-block {
    padding: 4px 14px 20px 14px;
  }

  .filters-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 12px;

    .filter+.filter {
      margin-top: 8px;
    }

    .filter-label {
      display: block;
      font-weight: 600;
      font-size: 12px;
      margin-bottom: 6px;
    }

    .date-input-sidebar {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid #d8d6de;
      border-radius: 4px;
      font-size: 14px;
      color: #6c757d;
      background-color: #fff;
      cursor: pointer;
      transition: border-color 0.2s;

      &:hover {
        border-color: #9f5714;
      }

      &:focus {
        outline: none;
        border-color: #9f5714;
        box-shadow: 0 0 0 2px rgba(243, 129, 26, 0.1);
      }
    }

    .quick-filter-buttons {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
    }

    .quick-filter-btn {
      padding: 6px 10px;
      border: 1px solid #d8d6de;
      border-radius: 4px;
      background-color: #fff;
      color: #6c757d;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #9f5714;
        color: #9f5714;
      }

      &.active {
        background-color: #9f5714;
        border-color: #9f5714;
        color: #fff;
      }
    }
  }

  .kpi-row {
    margin-bottom: 10px;
  }

  .kpi-card {
    text-align: center;
    padding-top: 10px;
  }

  .kpi-title {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .kpi-donut {
    display: flex;
    justify-content: center;
    position: relative;
  }

  .kpi-center {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    inset: 0;
  }

  .kpi-value {
    font-size: 18px;
    font-weight: 700;
    color: #4c4541;
  }

  .chart-row {
    margin-top: 10px;
  }

  .chart-card {
    height: 100%;
    padding: 12px;
  }

  .chart-header {
    font-weight: 700;
    margin-bottom: 4px;
  }

  .btn-filters {
    border-color: #9f5714 !important;
    color: #9f5714 !important;

    &:hover {
      background-color: #9f5714 !important;
      color: #fff !important;
    }
  }

  ::v-deep .b-sidebar-footer {
    .btn-primary {
      background-color: #9f5714 !important;
      border-color: #9f5714 !important;

      &:hover {
        background-color: #7d4510 !important;
        border-color: #7d4510 !important;
      }
    }
  }
}

@media (max-width: 767.98px) {
  #quality-page {
    .body-block {
      padding: 4px 8px 12px 8px;
    }

    .kpi-title {
      font-size: 11px;
    }

    .kpi-value {
      font-size: 13px;
    }

    .chart-header {
      font-size: 12px;
    }
  }
}
</style>
